import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Globe, BookOpen, Users } from 'lucide-react';

const impactAreas = [
  {
    icon: BookOpen,
    title: 'Bridging the AI Education Gap',
    description: 'Providing accessible AI education in Africa and developing regions',
  },
  {
    icon: Globe,
    title: 'Democratizing Access',
    description: 'Making future-of-finance tools and knowledge available to everyone',
  },
  {
    icon: Users,
    title: 'Empowering Individuals',
    description: 'Enabling participation in the global digital economy',
  },
];

export function ImpactSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      id="impact" 
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="impact-section"
    >
      {/* Background hairline */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-surface/30 to-transparent" />
      
      <div ref={ref} className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left - Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative order-2 lg:order-1"
          >
            <div className="relative rounded-md overflow-hidden aspect-[4/3] border border-line-1">
              <img
                src="https://images.pexels.com/photos/5621976/pexels-photo-5621976.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Global Community"
                className="w-full h-full object-cover grayscale-[40%] hover:grayscale-0 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent" />
            </div>
            
            {/* Floating stat card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="absolute -bottom-6 -right-6 md:right-8 bg-surface border border-line-1 rounded-md p-6"
            >
              <p className="eyebrow text-signal mb-1">FOCUS</p>
              <p className="text-3xl font-heading font-bold text-stark tracking-tight">Global</p>
              <p className="text-sm text-stark-2">Impact</p>
            </motion.div>
          </motion.div>
          
          {/* Right - Content */}
          <div className="order-1 lg:order-2">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <p className="eyebrow section-eyebrow-line mb-6">
                Global Impact
              </p>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-stark mb-6 tracking-tight">
                Committed to{' '}
                <span className="text-gradient">Meaningful Change</span>
              </h2>
              <p className="text-lg text-stark-2 mb-10 leading-relaxed">
                ServeNetics is deeply committed to creating lasting impact where 
                it matters most — empowering underserved communities with the tools 
                and knowledge to thrive in the digital age.
              </p>
            </motion.div>
            
            {/* Impact Areas */}
            <div className="space-y-px bg-line-1 border border-line-1 rounded-md overflow-hidden">
              {impactAreas.map((area, index) => (
                <motion.div
                  key={area.title}
                  initial={{ opacity: 0, x: 30 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1, ease: [0.16, 1, 0.3, 1] }}
                  className="flex items-start gap-4 p-5 bg-surface hover:bg-surface-2 transition-colors duration-300"
                  data-testid={`impact-area-${index}`}
                >
                  <div className="w-10 h-10 rounded-md bg-ink-2 border border-line-1 flex items-center justify-center flex-shrink-0">
                    <area.icon className="w-5 h-5 text-stark" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-stark mb-1 tracking-tight">
                      {area.title}
                    </h3>
                    <p className="text-sm text-stark-2 leading-relaxed">
                      {area.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ImpactSection;
